/* Monthly Tracker — 12-row table for a single year.
 * Auto-populates Leads Generated / Contracts Signed / Deals Closed from CRM;
 * the operator types the rest. Net profit + conversion ratios compute live. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, Download, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { downloadCsv, downloadAnnualReport } from "./reports";

const money = (n) => `$${(+n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (n) => `${((+n || 0) * 100).toFixed(1)}%`;

function derive(row) {
  const net = (+row.gross_revenue || 0) - (+row.lead_spend || 0) - (+row.operating_expenses || 0);
  const costPerDeal = row.deals_closed > 0
    ? ((+row.lead_spend || 0) + (+row.operating_expenses || 0)) / row.deals_closed : 0;
  const leadToContract = row.leads_generated > 0
    ? (+row.contracts_signed || 0) / row.leads_generated : 0;
  const contractToClose = row.contracts_signed > 0
    ? (+row.deals_closed || 0) / row.contracts_signed : 0;
  return { net, costPerDeal, leadToContract, contractToClose };
}

export default function MonthlyTrackerTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async (y) => {
    try {
      const { data } = await api.get(`/command-center/monthly?year=${y}`);
      setRows((data.items || []).map((r) => ({ ...r, _dirty: false })));
    } catch { toast.error("Could not load monthly tracker."); }
  };
  useEffect(() => { load(year); }, [year]);

  const patch = (month, field, val) => {
    setRows((prev) => prev.map((r) => r.month === month ? { ...r, [field]: val, _dirty: true } : r));
  };

  const save = async (row) => {
    setBusy(true);
    try {
      await api.patch(`/command-center/monthly/${year}/${row.month}`, {
        leads_generated: +row.leads_generated || 0,
        appointments: +row.appointments || 0,
        offers_made: +row.offers_made || 0,
        contracts_signed: +row.contracts_signed || 0,
        deals_closed: +row.deals_closed || 0,
        gross_revenue: +row.gross_revenue || 0,
        lead_spend: +row.lead_spend || 0,
        operating_expenses: +row.operating_expenses || 0,
      });
      setRows((prev) => prev.map((r) => r.month === row.month ? { ...r, _dirty: false } : r));
      toast.success(`${row.month_name} saved.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const totals = rows.reduce((acc, r) => {
    const d = derive(r);
    acc.leads += +r.leads_generated || 0;
    acc.closed += +r.deals_closed || 0;
    acc.gross += +r.gross_revenue || 0;
    acc.leadSpend += +r.lead_spend || 0;
    acc.opex += +r.operating_expenses || 0;
    acc.net += d.net;
    return acc;
  }, { leads: 0, closed: 0, gross: 0, leadSpend: 0, opex: 0, net: 0 });

  return (
    <div className="space-y-4" data-testid="monthly-tracker-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={() => setYear((y) => y - 1)} className="btn-outline p-2"
                data-testid="monthly-year-prev"><ChevronLeft size={16} /></button>
        <div className="font-display font-black text-2xl tabular-nums" data-testid="monthly-year">{year}</div>
        <button type="button" onClick={() => setYear((y) => y + 1)} className="btn-outline p-2"
                data-testid="monthly-year-next"><ChevronRight size={16} /></button>
        <div className="text-xs text-[var(--ink-3)] ml-2 flex-1 min-w-[220px]">
          Auto-populated columns pull from your CRM. Override anything by typing over it.
        </div>
        <button type="button" onClick={() => downloadCsv("monthly", { year })}
                className="btn-outline" data-testid="monthly-download-csv">
          <Download size={14} /> Download CSV
        </button>
        <button type="button" onClick={() => downloadAnnualReport(year)}
                className="btn-money" data-testid="monthly-download-pdf">
          <FileText size={14} /> Annual Business Report (PDF)
        </button>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm" data-testid="monthly-tracker-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--ink-3)] border-b border-[var(--line)]">
              <th className="p-2 min-w-[100px]">Month</th>
              <th className="p-2">Leads*</th>
              <th className="p-2">Appts</th>
              <th className="p-2">Offers</th>
              <th className="p-2">Contracts*</th>
              <th className="p-2">Closed*</th>
              <th className="p-2">Gross ($)</th>
              <th className="p-2">Lead spend</th>
              <th className="p-2">Op-ex</th>
              <th className="p-2">Net</th>
              <th className="p-2">$/deal</th>
              <th className="p-2">L→C%</th>
              <th className="p-2">C→Close%</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const d = derive(r);
              return (
                <tr key={r.month} className={`border-b border-[var(--line)] ${r._dirty ? "bg-amber-50" : ""}`}
                    data-testid={`monthly-row-${r.month}`}>
                  <td className="p-2 font-semibold">{r.month_name}</td>
                  <NumCell val={r.leads_generated} onChange={(v) => patch(r.month, "leads_generated", v)} />
                  <NumCell val={r.appointments} onChange={(v) => patch(r.month, "appointments", v)} />
                  <NumCell val={r.offers_made} onChange={(v) => patch(r.month, "offers_made", v)} />
                  <NumCell val={r.contracts_signed} onChange={(v) => patch(r.month, "contracts_signed", v)} />
                  <NumCell val={r.deals_closed} onChange={(v) => patch(r.month, "deals_closed", v)} />
                  <NumCell val={r.gross_revenue} onChange={(v) => patch(r.month, "gross_revenue", v)} width={100} />
                  <NumCell val={r.lead_spend} onChange={(v) => patch(r.month, "lead_spend", v)} width={90} />
                  <NumCell val={r.operating_expenses} onChange={(v) => patch(r.month, "operating_expenses", v)} width={90} />
                  <td className={`p-2 font-semibold ${d.net > 0 ? "text-emerald-600" : d.net < 0 ? "text-rose-600" : ""}`}>
                    {money(d.net)}
                  </td>
                  <td className="p-2 text-[var(--ink-2)]">{money(d.costPerDeal)}</td>
                  <td className="p-2 text-[var(--ink-2)]">{pct(d.leadToContract)}</td>
                  <td className="p-2 text-[var(--ink-2)]">{pct(d.contractToClose)}</td>
                  <td className="p-2">
                    {r._dirty && (
                      <button type="button" onClick={() => save(r)} disabled={busy}
                              className="btn-outline text-[var(--primary)] p-2" title="Save">
                        <Save size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-[var(--muted)]">
            <tr className="border-t-2 border-[var(--line)]">
              <td className="p-2 font-bold">{year} Total</td>
              <td className="p-2 font-display font-black">{totals.leads}</td>
              <td colSpan={2}></td>
              <td className="p-2"></td>
              <td className="p-2 font-display font-black">{totals.closed}</td>
              <td className="p-2 font-display font-black">{money(totals.gross)}</td>
              <td className="p-2">{money(totals.leadSpend)}</td>
              <td className="p-2">{money(totals.opex)}</td>
              <td className={`p-2 font-display font-black ${totals.net > 0 ? "text-emerald-600" : totals.net < 0 ? "text-rose-600" : ""}`}>
                {money(totals.net)}
              </td>
              <td colSpan={4}></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-[var(--ink-3)]">
        * = auto-populated from CRM. You can still override any number by typing over it.
      </p>
    </div>
  );
}

function NumCell({ val, onChange, width = 70 }) {
  return (
    <td className="p-2">
      <input type="number" className="form-input" value={val ?? 0} min={0} step={1}
             onChange={(e) => onChange(e.target.value)} style={{ width }} />
    </td>
  );
}
